/**
 * Legenda-filtro dos PINS do mapa (M2). Cada item (nível e tipo) é um toggle.
 * Começa com tudo marcado; desmarcar esconde os pins correspondentes.
 *
 * GUPM-194: os toggles de CAMADA (mancha/sirenes/residências) migraram para a
 * barra de controles (MapControls, acima do mapa, fiel ao V8). Aqui fica só a
 * legenda dos pins de instrumento, abaixo do mapa.
 */
import { LEVEL_COLOR, NIVEL_EXIBIDO_ORDER, TYPE_MARKER } from '../domain/pins';
import {
  FORA_DE_SERVICO, SEM_FAIXA, SEM_LEITURA, SEM_SINAL, type NivelExibido,
} from '@/domain/instrumentos/nivel-exibido';
import type { InstrumentType } from '@/domain';

const LEVEL_LABEL: Record<NivelExibido, string> = {
  NORMAL: 'Normal',
  ATENCAO: 'Atenção',
  ALERTA: 'Alerta',
  EMERGENCIA: 'Emergência',
  // GUPM-228 — entrada própria na legenda: sem ela, instrumento sem leitura
  // sumiria do mapa ao filtrar por nível.
  [SEM_LEITURA]: 'Sem leitura',
  // GUPM-247 — idem: sem entrada na legenda, os 43 sem faixa sumiriam do mapa
  // ao filtrar por nível (nenhum dos 4 casa com eles).
  // Rótulo curto no chip do mapa ("Sem limite definido" no resto do app).
  [SEM_FAIXA]: 'Sem limite',
  // GUPM-303 — sem entrada na legenda, o tiltimetro mudo sumiria do mapa ao filtrar
  // por nivel (nenhum dos outros casa com ele) — mesmo defeito do GUPM-247.
  [SEM_SINAL]: 'Sem sinal',
  // GUPM-307 — mesma razao: sem entrada na legenda, o instrumento declarado fora de
  // servico sumiria do mapa ao filtrar por nivel.
  [FORA_DE_SERVICO]: 'Fora de servico',
};

/** Camadas PAEBM alternáveis — tipo compartilhado com MapControls (dono dos toggles). */
export type MapLayer = 'sirenes' | 'edificacoes' | 'pontosEncontro' | 'mancha' | 'zss';

export interface MapLegendProps {
  presentTypes: InstrumentType[];
  activeLevels: ReadonlySet<NivelExibido>;
  activeTypes: ReadonlySet<InstrumentType>;
  onToggleLevel: (level: NivelExibido) => void;
  onToggleType: (type: InstrumentType) => void;
  onSetAllLevels: (on: boolean) => void;
  onSetAllTypes: (on: boolean) => void;
}

function chipClass(active: boolean): string {
  return [
    'flex items-center gap-1.5 px-2 py-1 rounded-full border text-[11px] transition',
    active
      ? 'border-jf-border bg-jf-surface2 text-white'
      : 'border-jf-border/50 text-jf-dim opacity-50 line-through',
  ].join(' ');
}

export default function MapLegend({
  presentTypes,
  activeLevels,
  activeTypes,
  onToggleLevel,
  onToggleType,
  onSetAllLevels,
  onSetAllTypes,
}: MapLegendProps) {
  return (
    <div className="mt-2 space-y-1.5">
      <Row
        label="Níveis"
        onAll={() => onSetAllLevels(true)}
        onNone={() => onSetAllLevels(false)}
      >
        {NIVEL_EXIBIDO_ORDER.map((level) => {
          const active = activeLevels.has(level);
          return (
            <button
              key={level}
              type="button"
              aria-pressed={active}
              onClick={() => onToggleLevel(level)}
              className={chipClass(active)}
            >
              <span
                className="inline-block w-2.5 h-2.5 rounded-full"
                style={{ background: LEVEL_COLOR[level] }}
              />
              {LEVEL_LABEL[level]}
            </button>
          );
        })}
      </Row>

      <Row
        label="Tipos"
        onAll={() => onSetAllTypes(true)}
        onNone={() => onSetAllTypes(false)}
      >
        {presentTypes.map((type) => {
          const active = activeTypes.has(type);
          return (
            <button
              key={type}
              type="button"
              aria-pressed={active}
              onClick={() => onToggleType(type)}
              className={chipClass(active)}
            >
              <span aria-hidden>{TYPE_MARKER[type].symbol}</span>
              {type}
            </button>
          );
        })}
      </Row>
    </div>
  );
}

function Row({
  label,
  onAll,
  onNone,
  children,
}: {
  label: string;
  onAll?: () => void;
  onNone?: () => void;
  children: React.ReactNode;
}) {
  const lower = label.toLowerCase();
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-[10px] uppercase tracking-wider text-jf-dim w-12 shrink-0">{label}</span>
      {children}
      {onAll && onNone && (
        <span className="text-[10px] text-jf-dim ml-1">
          <button
            type="button"
            onClick={onAll}
            aria-label={`Selecionar todos os ${lower}`}
            className="hover:text-jf-yellow"
          >
            tudo
          </button>
          {' · '}
          <button
            type="button"
            onClick={onNone}
            aria-label={`Limpar todos os ${lower}`}
            className="hover:text-jf-yellow"
          >
            limpar
          </button>
        </span>
      )}
    </div>
  );
}
